import React, { useState, useEffect } from 'react';
import { useLocation, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Timeline as TimelineIcon,
  PersonSearch as PersonSearchIcon,
  LocationOn as LocationIcon,
  Logout as LogoutIcon,
  MenuBook as MenuBookIcon,
  SpaceDashboard as SpaceDashboardIcon,
  GppGood as GppGoodIcon,
  ManageSearchOutlined as ManageSearchOutlinedIcon,
  Code as CodeIcon,
  Home as HomeIcon,
  Check as CheckIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { styled } from '@mui/material/styles';
import NoAccessMessage from '../components/NoAccessMessage';
import axios from 'axios';
import { useSubscriptionContext } from '../context/SubscriptionContext';
import SubscriptionStatusBadge from '../components/SubscriptionStatusBadge';
import { useEnterprise } from '../hooks/useEnterprise';
import { handleApiError } from '../utils/errorHandling';
import SessionExpiredMessage from '../components/SessionExpiredMessage';
import { shouldShowNoWorkspacesCard, useOrganizationsContext } from '../context/OrganizationsContext';
import IntercomChatButton from '../components/IntercomChatButton';

const drawerWidth = 200;

const Logo = styled('img')({
  height: '29px',
  margin: '16px auto',
  display: 'block',
});

interface MainLayoutProps {
  children: React.ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logout, user, isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { organizations, invitedOrganizations, currentOrganizationId, loading, error: organizationsError, initialDataLoaded, setDefaultOrganization, refetch } = useOrganizationsContext();
  
  // Compute whether to show "Set up your first Workspace" message
  const shouldShowSetupMessage = shouldShowNoWorkspacesCard({
    loading,
    error: organizationsError,
    initialDataLoaded,
    organizations,
    invitedOrganizations,
  }) && !currentOrganizationId;
  const { enterpriseStatus } = useEnterprise();
  const open = Boolean(anchorEl);
  const [subscriptionUpdating, setSubscriptionUpdating] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  
  // Get current subscription info
  const { subscription, enterprise, loading: subscriptionLoading, refetch: refetchSubscription } = useSubscriptionContext();
  const hasSubscription = subscription?.subscription?.id != null;
  const currentPlan = {
    tier: enterprise ? 'enterprise' : (subscription?.tier || (hasSubscription ? 'free' : 'none')),
    isActive: enterprise ? true : (subscription?.subscription?.status === 'active'),
    planName: enterprise ? 'Enterprise' : (hasSubscription ? ((subscription?.tier || 'free').charAt(0).toUpperCase() + (subscription?.tier || 'free').slice(1)) : 'None'),
    subscriptionId: subscription?.subscription?.id || null,
    nextBillingDate: subscription?.subscription?.current_period_end
      ? new Date(subscription.subscription.current_period_end * 1000).toLocaleDateString()
      : null,
    willCancel: Boolean(subscription?.subscription?.cancel_at_period_end),
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    // Blur any focused elements in the drawer before opening the menu
    // This prevents aria-hidden warning when Material-UI sets aria-hidden on root
    const activeElement = document.activeElement;
    if (activeElement && activeElement instanceof HTMLElement) {
      // Check if the focused element is in the drawer (navigation items)
      const drawerElement = document.querySelector('.MuiDrawer-root');
      if (drawerElement && drawerElement.contains(activeElement)) {
        activeElement.blur();
      }
    }
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    // Move focus away from menu items before closing to prevent aria-hidden warning
    // This ensures focus is removed before Material-UI sets aria-hidden on the menu
    const activeElement = document.activeElement;
    if (activeElement && activeElement instanceof HTMLElement) {
      // Only blur if the active element is a menu item or inside a menu
      const isMenuItem = activeElement.classList.contains('MuiMenuItem-root');
      const menuElement = document.querySelector('[role="menu"]');
      if (isMenuItem || (menuElement && menuElement.contains(activeElement))) {
        activeElement.blur();
      }
    }
    setAnchorEl(null);
  };

  const isAdmin = user && Array.isArray(user["https://bespot.com/roles"]) && (user["https://bespot.com/roles"].includes("admin") || user["https://bespot.com/roles"].includes("support"));

  // Check if user has no roles
  const hasNoRoles = !user || !user["https://bespot.com/roles"] || user["https://bespot.com/roles"].length === 0;

  // Initialize provider data when user becomes available
  useEffect(() => {
    if (user && isAuthenticated) {
      refetch(true);
    }
  }, [user, isAuthenticated, refetch]);

  useEffect(() => {
    const handleSubscriptionChange = (event: CustomEvent) => {
      setSubscriptionUpdating(true);
      // Immediately try to refresh the subscription to prevent Free fallback
      try { refetchSubscription(true); } catch {}
      // Reset updating state after a short delay
      setTimeout(() => setSubscriptionUpdating(false), 2000);
      // Do a second refresh after caches settle
      setTimeout(() => { try { refetchSubscription(true); } catch {} }, 1500);
    };

    window.addEventListener('subscriptionChanged', handleSubscriptionChange as EventListener);
    const onRefreshTokenError = () => setShowSessionExpired(true);
    window.addEventListener('refreshTokenError', onRefreshTokenError as EventListener);
    // Fix badge staleness after browser Back/Forward cache or history navigation
    const onPopState = () => { try { refetchSubscription(true); } catch {} };
    const onPageShow = () => { try { refetchSubscription(true); } catch {} };
    window.addEventListener('popstate', onPopState);
    window.addEventListener('pageshow', onPageShow);
    
    return () => {
      window.removeEventListener('subscriptionChanged', handleSubscriptionChange as EventListener);
      window.removeEventListener('refreshTokenError', onRefreshTokenError as EventListener);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  const handleSetDefaultOrganization = async (orgId: string) => {
    await setDefaultOrganization(orgId);
    
    // Dispatch custom event to notify other components
    const event = new CustomEvent('organizationChanged', {
      detail: { defaultOrganizationId: orgId }
    });
    window.dispatchEvent(event);
    
    handleMenuClose();
  };

  const getCurrentOrganizationName = () => {
    const getOrgLabel = (org: any) => org?.name || org?.organization_name || org?.id || 'Unknown workspace';

    // If still loading, show loading state
    if (loading) {
      return 'Loading...';
    }

    // If we have organizations or invited organizations
    if (organizations.length > 0 || invitedOrganizations.length > 0) {
      // First, try to find the organization by currentOrganization state
      if (currentOrganizationId) {
        const org = organizations.find(o => o.id === currentOrganizationId);
        if (org) {
          return getOrgLabel(org);
        }
        // Try invited organizations
        const inv = invitedOrganizations.find(o => o.id === currentOrganizationId);
        if (inv) {
          return getOrgLabel(inv);
        }
      }

      // Then, try to find by user's app_metadata default_organization_id
      if (user?.app_metadata?.default_organization_id) {
        const org = organizations.find(o => o.id === user.app_metadata.default_organization_id);
        if (org) {
          return getOrgLabel(org);
        }
        const inv = invitedOrganizations.find(o => o.id === user.app_metadata.default_organization_id);
        if (inv) {
          return getOrgLabel(inv);
        }
      }

      // If we have any entries, fallback to the first available
      if (organizations.length > 0) return getOrgLabel(organizations[0]);
      if (invitedOrganizations.length > 0) return getOrgLabel(invitedOrganizations[0]);
    }

    // Only show "No organization yet" when we're sure there are no organizations
    // and we're not in a loading state
    return 'No organization yet';
  };

  if (hasNoRoles) {
    return <NoAccessMessage />;
  }

  // Define menu items organized by sections
  const lobbyItem = { text: 'Home', icon: <HomeIcon />, path: '/home' };
  
  const analyticsItems = [
    { text: 'Overview', icon: <SpaceDashboardIcon />, path: '/overview' },
    { text: 'Activity', icon: <TimelineIcon />, path: '/activity' },
    { text: 'Rules', icon: <GppGoodIcon />, path: '/rules' },
    { text: 'Users', icon: <PersonSearchIcon />, path: '/users' },
    { text: 'Investigation', icon: <ManageSearchOutlinedIcon />, path: '/investigation' },
    { text: 'Location Insights', icon: <LocationIcon />, path: '/location-insights' },
  ];

  const settingsItem = { text: 'Settings', icon: <SettingsIcon />, path: '/settings' };

  // Combine all menu items (Administration, Billing, and Access moved to Settings)
  const allMenuItems = [
    lobbyItem,
    ...analyticsItems,
    settingsItem,
  ];

  const backgroundColor = '#F6F6F6';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  // Treat Settings as "active" for all settings-related subroutes
  const settingsRelatedPaths = [
    '/settings',
    '/workspaces',
    '/api-keys',
    '/policy-configuration',
    '/geofence-manager',
    '/subscription',
    '/business-profile',
    '/pricing',
    '/invite-users',
    '/my-collaborations',
    '/configurations',
    '/available-rules',
    '/available-rule-types',
    '/rule-type-builder',
    '/health-status-monitoring',
    '/security-monitoring',
    '/superadmin/loops-test',
    '/support-investigation',
  ];

  const isSettingsRoute = settingsRelatedPaths.some((path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  const drawer = (
    <Box sx={{ 
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#FFFFFF',
    }}>
      <Box sx={{ 
        height: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Logo src="/gatekeeper_logo.png" alt="Gatekeeper" />
      </Box>
      <List sx={{ 
        flex: 1,
        display: 'flex', 
        flexDirection: 'column',
        pb: 0,
        mt: 0,
        overflow: 'hidden',
        maxWidth: drawerWidth,
      }}>
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {/* Lobby Section */}
          <ListItem
            key="lobby"
            disablePadding
            sx={{ mx: 0, px: 1, borderRadius: 1, mb: 0.5 }}
          >
            <ListItemButton
              component={RouterLink}
              to={lobbyItem.path}
              selected={location.pathname === lobbyItem.path}
              sx={{
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                maxWidth: drawerWidth,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.16)',
                  },
                  '& .MuiListItemIcon-root': {
                    color: '#1976d2',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 32, '& svg': { width: '0.9em', height: '0.9em' } }}>
                {lobbyItem.icon}
              </ListItemIcon>
              <ListItemText 
                primary={lobbyItem.text}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: location.pathname === lobbyItem.path ? 500 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* Analytics Section */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 0.5,
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Analytics
            </Typography>
          </Box>
          {analyticsItems.map((item) => (
            <ListItem
              key={item.text}
              disablePadding
              sx={{ mx: 0, px: 1, borderRadius: 1, mb: 0.5 }}
            >
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  maxWidth: drawerWidth,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.16)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#1976d2',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 32, '& svg': { width: '0.9em', height: '0.9em' } }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: location.pathname === item.path ? 500 : 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

        </Box>
        <Box sx={{ 
          bgcolor: '#FFFFFF',
          borderTop: 1,
          borderColor: 'divider',
          pt: 1,
          flexShrink: 0,
        }}>
          <ListItemButton
            onClick={() => window.open('https://docs.bespot.com/', '_blank')}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 32, '& svg': { width: '0.9em', height: '0.9em' } }}>
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Documentation"
              primaryTypographyProps={{
                fontSize: '0.875rem',
              }}
            />
          </ListItemButton>
          <ListItemButton
            onClick={() => window.open('https://support.bespot.com/', '_blank')}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 32, '& svg': { width: '0.9em', height: '0.9em' } }}>
              <HelpOutlineIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Help Center"
              primaryTypographyProps={{
                fontSize: '0.875rem',
              }}
            />
          </ListItemButton>
          <ListItemButton
            component={RouterLink}
            to="/settings"
            selected={isSettingsRoute}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              maxWidth: drawerWidth,
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.16)',
                },
                '& .MuiListItemIcon-root': {
                  color: '#1976d2',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 32, '& svg': { width: '0.9em', height: '0.9em' } }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Settings"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                  fontWeight: isSettingsRoute ? 500 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            />
          </ListItemButton>
          {/* Legal links under Logout */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', mb: 0.75 }}>
              © 2025 Bespot. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <RouterLink 
                to="/terms-of-use" 
                style={{ textDecoration: 'none', color: 'inherit', fontSize: '0.65rem' }}
              >
                Terms of Use
              </RouterLink>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>||</Typography>
              <RouterLink 
                to="/privacy-policy" 
                style={{ textDecoration: 'none', color: 'inherit', fontSize: '0.65rem' }}
              >
                Privacy Policy
              </RouterLink>
            </Box>
          </Box>
        </Box>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {showSessionExpired && (
        <SessionExpiredMessage onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })} />
      )}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: '#FFFFFF',
          boxShadow: 'none',
          borderBottom: 1,
          borderColor: 'divider',
          height: '80px',
          justifyContent: 'center',
          color: 'text.primary',
          ml: { sm: `${drawerWidth / 2}px` },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {allMenuItems.find(item => item.path === location.pathname)?.text || 
             (location.pathname === '/settings' ? 'Settings' : 'Overview')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Plan Indicator */}
            {subscriptionUpdating ? (
              <Chip
                label="Updating..."
                size="small"
                variant="outlined"
                icon={<CircularProgress size={14} sx={{ mr: -0.5 }} />}
                sx={{ 
                  fontSize: '0.75rem',
                  height: '28px',
                  fontWeight: 500,
                }}
              />
            ) : (
              <SubscriptionStatusBadge showDetails={true} />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography variant="body2" color="text.secondary">
                Workspace
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {getCurrentOrganizationName()}
              </Typography>
            </Box>
            {/* Settings Icon */}
            <IconButton
              color="inherit"
              onClick={() => navigate('/settings')}
              sx={{ p: 1, ml: 1 }}
              title="Settings"
            >
              <SettingsIcon />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ p: 0, ml: 1 }}
            >
              <Avatar
                src={user?.picture || undefined}
                alt={user?.name || user?.email || 'Anonymous'}
                sx={{ width: 36, height: 36 }}
              />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              disableAutoFocusItem={true}
              disableEnforceFocus={true}
              disableRestoreFocus={true}
              MenuListProps={{
                'aria-labelledby': 'menu-button',
                onKeyDown: (e) => {
                  // Prevent aria-hidden warning by ensuring focus is managed properly
                  if (e.key === 'Escape') {
                    handleMenuClose();
                  }
                },
              }}
              PaperProps={{
                elevation: 3,
                sx: { mt: 1.5, minWidth: 240, maxHeight: 350 },
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {/* User identity at top */}
              <MenuItem disabled sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {user?.name || user?.email || 'User'}
                  </Typography>
                  {!!user?.email && user?.name && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {user.email}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem disabled sx={{ py: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Workspaces
                </Typography>
              </MenuItem>
              <Divider />
              {shouldShowSetupMessage ? (
                <MenuItem key="no-workspaces" disabled sx={{ py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {loading ? 'Loading workspaces...' : 'Set up your first Workspace'}
                  </Typography>
                </MenuItem>
              ) : [
                ...organizations
                  .filter((org) => !invitedOrganizations.some((inv) => inv.id === org.id))
                  .map((org) => (
                  <MenuItem
                    key={org.id}
                    onClick={() => handleSetDefaultOrganization(org.id)}
              sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.5,
                      px: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
                    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                      {org.name || org.organization_name || org.id}
                    </Typography>
                    {currentOrganizationId === org.id && (
                      <CheckIcon 
                        fontSize="small" 
                        color="primary"
                        sx={{ ml: 0.5, fontSize: '0.8rem' }}
                      />
                    )}
                  </MenuItem>
                )),
                ...(invitedOrganizations.length > 0 ? [
                  <MenuItem key="shared-header" disabled sx={{ py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Shared Workspaces
                    </Typography>
                  </MenuItem>,
                  ...invitedOrganizations.map((org) => (
                    <MenuItem
                      key={`inv-${org.id}`}
                      onClick={() => handleSetDefaultOrganization(org.id)}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                        px: 1.5,
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {org.name || (org as any).organization_name || org.id}
                      </Typography>
                      {currentOrganizationId === org.id && (
                        <CheckIcon fontSize="small" color="primary" sx={{ ml: 0.5, fontSize: '0.8rem' }} />
                      )}
                    </MenuItem>
                  ))
                ] : [])
              ]}
              <Divider />
              <MenuItem disabled sx={{ py: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Current Plan
                </Typography>
              </MenuItem>
              <MenuItem 
                disabled 
                sx={{ 
                  py: 1, 
                  px: 1.5,
                  backgroundColor: currentPlan.isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  borderRadius: 1,
                  mx: 0.5,
                  mb: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {(() => {
                    const raw = subscription?.plan_badge_color as string | undefined;
                    const faded = raw && /^#([0-9a-fA-F]{6})$/.test(raw) ? `${raw}26` : raw; // ~15% alpha
                    return (
                  <Chip
                    label={
                      subscriptionLoading ? 'Loading...' : 
                      subscriptionUpdating ? 'Updating...' : 
                      enterprise ? 'Enterprise' : (subscription?.plan_badge_name || currentPlan.planName)
                    }
                    size="small"
                    color={
                      (subscription?.plan_badge_color && !subscriptionLoading && !subscriptionUpdating) ? undefined :
                      (subscriptionLoading || subscriptionUpdating ? 'default' : (enterprise ? 'secondary' : (currentPlan.isActive ? 'success' : 'default')))
                    }
                    variant={(subscription?.plan_badge_color && !subscriptionLoading && !subscriptionUpdating) ? 'outlined' : (subscriptionLoading || subscriptionUpdating ? 'outlined' : (enterprise ? 'filled' : (currentPlan.isActive ? 'filled' : 'outlined')))}
                    icon={subscriptionUpdating ? <CircularProgress size={12} sx={{ mr: -0.5 }} /> : undefined}
                    sx={{ 
                      fontSize: '0.7rem',
                      height: '24px',
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: 1,
                      },
                      ...(subscription?.plan_badge_color && !subscriptionLoading && !subscriptionUpdating) && {
                        backgroundColor: faded,
                        color: '#000',
                        borderColor: 'divider',
                      },
                      ...(enterprise && !subscriptionLoading && !subscriptionUpdating) && {
                        background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                        color: 'white',
                        boxShadow: '0 1px 4px rgba(156, 39, 176, 0.3)',
                      },
                      ...(currentPlan.isActive && !enterpriseStatus && !subscriptionLoading && !subscriptionUpdating && !subscription?.plan_badge_color) && {
                        background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                        color: 'white',
                        boxShadow: '0 1px 4px rgba(46, 125, 50, 0.3)',
                      },
                    }}
                  />)} )()}
                  {currentPlan.isActive && !subscriptionLoading && !subscriptionUpdating && (
                    <CheckIcon 
                      fontSize="small" 
                      color="primary"
                      sx={{ fontSize: '0.8rem' }}
                    />
                  )}
                </Box>
              </MenuItem>
              {currentPlan.isActive && currentPlan.nextBillingDate && !subscriptionLoading && (
                <MenuItem disabled sx={{ py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Next billing: {currentPlan.nextBillingDate}
                  </Typography>
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={() => { navigate('/settings'); handleMenuClose(); }} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <SettingsIcon fontSize="small" sx={{ fontSize: '0.8rem' }} />
                </ListItemIcon>
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                  Settings
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <LogoutIcon fontSize="small" sx={{ fontSize: '0.8rem' }} />
                </ListItemIcon>
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                  Logout
                </Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              bgcolor: '#FFFFFF',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              bgcolor: '#FFFFFF',
              borderRight: 1,
              borderColor: 'divider',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: location.pathname === '/workspaces' ? 0 : 1,
          mt: (location.pathname === '/policy-configuration' || location.pathname === '/workspaces') ? '15px' : '35px',
          ml: { sm: (location.pathname === '/policy-configuration' || location.pathname === '/workspaces') ? `${(drawerWidth / 2) -40}px` : `${drawerWidth / 2}px` },
        }}
      >
        {children}
        <IntercomChatButton />
      </Box>
    </Box>
  );
} 